/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import {
  ChangeEvent,
  useState,
  forwardRef,
  Ref,
  RefAttributes,
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { FieldValues, Path, PathValue, useController, useWatch } from "react-hook-form";

import { getLabel, isInputRequired } from "../../../common/helpers/validators";
import { useCurrentInputVibe } from "../../../common/hooks/useCurrentInputVibe";
import { DashboardCheckbox } from "../DashboardCheckbox/DashboardCheckbox";
import { DashboardOptionGroupLabel } from "../OptionGroupLabel";

import {
  CheckboxGroupProps,
  BaseCheckboxGroupProps,
  FormCheckboxGroupProps,
  CheckboxGroupCheckboxProps,
} from "./types";

import { useFormInputRefs } from "@/common/hooks/useFormInputRefs";
import { useValidators } from "@/common/hooks/useValidators";

function CheckboxGroupInner<T extends FieldValues>(
  { control, name, ...rest }: CheckboxGroupProps<T>,
  ref: Ref<HTMLInputElement>
) {
  return control && name ? (
    <FormCheckboxGroup control={control} name={name} {...rest} ref={ref} />
  ) : (
    <BaseCheckboxGroup {...rest} name={name} ref={ref} />
  );
}

CheckboxGroupInner.displayName = "CheckboxGroup";

export const DashboardCheckboxGroup = forwardRef(CheckboxGroupInner) as <T extends FieldValues>(
  props: CheckboxGroupProps<T> & RefAttributes<HTMLButtonElement>
) => JSX.Element;

function FormCheckboxGroupInner<T extends FieldValues>(
  { control, defaultChecked, name, rules, onChange, noAsterisk, ...rest }: FormCheckboxGroupProps<T>,
  ref: Ref<HTMLInputElement>
) {
  const elementRef = useRef<HTMLElement | null>(null);
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);
  const watchedValue = useWatch({ control, name });
  const currentDefaultChecked = watchedValue ?? (defaultChecked as PathValue<T, Path<T>>);

  const { field } = useController<T>({
    name,
    control,
    defaultValue: currentDefaultChecked,
    rules: validators,
  });

  const handleChange = useCallback<NonNullable<BaseCheckboxGroupProps["onChange"]>>(
    (values: Record<string, boolean>) => {
      onChange?.(values);
    },
    [onChange]
  );

  const refCallBack = useFormInputRefs({
    inputRefCallBack: (element: HTMLInputElement | null) => {
      field.ref(element);
      elementRef.current = element;
    },
  });

  // use watchedValue instead of field.value because field.value is staled when using FormCheckboxGroup in the customAnswerOfParticipant
  const values: Record<string, boolean> = useMemo(() => {
    return (watchedValue as string[])?.reduce(
      (prev, current) => {
        return { ...prev, [current]: true };
      },
      {} as Record<string, boolean>
    );
  }, [watchedValue]);

  /* Workaround to scroll checkbox element into view when focused on iOS */
  useEffect(() => {
    const element = elementRef?.current;

    if (!element) {
      return;
    }

    let wasClicked = false;

    const handlePress = () => {
      wasClicked = true;
    };

    const handleFocus = () => {
      if (!wasClicked) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      // Reset the flag after handling focus
      wasClicked = false;
    };

    element.addEventListener("mousedown", handlePress);
    element.addEventListener("touchstart", handlePress);
    element.addEventListener("focus", handleFocus);

    return () => {
      element.removeEventListener("mousedown", handlePress);
      element.removeEventListener("touchstart", handlePress);
      element.removeEventListener("focus", handleFocus);
    };
  }, []);

  // This is an uncontrolled component, and that changing its value in the form directly (using setValue for example), will not change what's rendered in the DOM.
  return (
    <BaseCheckboxGroup
      {...rest}
      ref={ref}
      inputRef={refCallBack}
      onChange={handleChange}
      label={getLabel(isRequired, rest.label, noAsterisk)}
      name={name}
      values={values}
    />
  );
}

FormCheckboxGroupInner.displayName = "FormCheckboxGroup";

const FormCheckboxGroup = forwardRef(FormCheckboxGroupInner) as unknown as <T extends FieldValues>(
  props: FormCheckboxGroupProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

const BaseCheckboxGroup = forwardRef<HTMLInputElement, BaseCheckboxGroupProps>(
  ({
    checkboxes,
    className,
    vibe,
    defaultChecked = {},
    disabled = false,
    error,
    errorMessage,
    helperText,
    label,
    size = "small",
    tooltipText,
    onChange,
    "data-test": dataTest,
    inputRef,
    name,
    values,
  }) => {
    const currentInputVibe = useCurrentInputVibe(vibe);

    const getInitialDefaultChecked = useCallback((): Record<string, boolean> => {
      const checkboxesDefaultChecked = checkboxes.reduce(
        (prev: Record<string, boolean>, { choice, defaultChecked: checkboxDefaultChecked }) => {
          return { ...prev, [choice]: checkboxDefaultChecked ?? false };
        },
        {}
      );
      return { ...checkboxesDefaultChecked, ...defaultChecked };
    }, [checkboxes, defaultChecked]);

    const [internalValues, setInternalValues] = useState<Record<string, boolean>>(getInitialDefaultChecked());

    const currentValues = values ?? internalValues;

    const handleChange = useCallback(
      (checkbox: CheckboxGroupCheckboxProps): NonNullable<CheckboxGroupCheckboxProps["onChange"]> =>
        (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
          const updateValues = { ...currentValues, [checkbox.choice]: checked };
          onChange?.(updateValues);
          checkbox?.onChange?.(event, checked);
          setInternalValues(updateValues);
        },
      [onChange, currentValues]
    );

    return (
      <DashboardOptionGroupLabel
        className={className}
        vibe={currentInputVibe}
        data-test={dataTest}
        error={error}
        errorMessage={errorMessage}
        helperText={helperText}
        label={label}
        tooltipText={tooltipText}
      >
        {checkboxes.map((checkbox, index) => {
          const {
            choice,
            className: checkboxClassName,
            vibe: checkboxVibe,
            disabled: checkboxDisabled,
            ...restOfCheckboxProps
          } = checkbox;
          const value = currentValues[choice];
          return (
            <DashboardCheckbox
              key={choice}
              data-test="checkbox"
              {...restOfCheckboxProps}
              style={{
                ...(index !== 0 ? { marginTop: "4px" } : {}),
                ...restOfCheckboxProps.style,
              }}
              className={checkboxClassName}
              vibe={checkboxVibe ?? currentInputVibe}
              defaultChecked={value}
              value={value}
              checked={Boolean(value)}
              disabled={checkboxDisabled || disabled}
              onChange={handleChange(checkbox)}
              size={size}
              inputRef={inputRef}
              name={name}
            />
          );
        })}
      </DashboardOptionGroupLabel>
    );
  }
);

BaseCheckboxGroup.displayName = "CheckboxGroup";
